import { Link, useLocation, useNavigate } from "react-router-dom";
import menusData from "./menus";
import "./sidebar.scss";
import LaxmiLogo from "../../assets/laxmi.svg";
import UserAdmin from "../../assets/images/user.svg";
import { ReactComponent as IconLogout } from "../../assets/icons/icon_logout.svg";
import { FABRIC } from "../../routes/routes_name";
import { useEffect, useState } from "react";
import { loginData, logout } from "../../stores/auth";
import { useDispatch, useSelector } from "react-redux";
import { CapitalizeWords } from "../../helpers";

const SideBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menus, setMenus] = useState(menusData);
  const dataLogin = useSelector(loginData);

  const location = useLocation();

  // Helper function to check if user has access to a feature
  const hasFeatureAccess = (feature) => {
    if (!feature || dataLogin?.data?.features_access_user?.all) {
      return true;
    }

    const features = dataLogin?.data?.features_access_user;
    if (!features) return false;

    // For nested features like "master.administrator"
    if (feature.includes('.')) {
      const [parent, child] = feature.split('.');
      return features[parent]?.[child] === true;
    }

    // For parent features (like "master"), check if it's an object with nested properties
    // or a boolean that's true
    const featureValue = features[feature];
    if (typeof featureValue === 'object' && featureValue !== null) {
      // It's a parent with nested features, allow if any nested feature is accessible
      return true;
    }
    
    // For top-level features that are boolean
    return featureValue === true;
  };

  // Helper to check if a parent menu should show based on accessible submenus
  const hasAccessibleSubmenus = (menu) => {
    if (!menu.subMenus) return false;
    return menu.subMenus.some(subMenu => hasFeatureAccess(subMenu.feature));
  };

  // Filter menus based on feature access
  const getFilteredMenus = () => {
    return menusData
      .filter(menu => {
        // For menus with submenus, show if user has access to any submenu
        if (menu.subMenus) {
          return hasAccessibleSubmenus(menu);
        }
        // For regular menus, check feature access
        return hasFeatureAccess(menu.feature);
      })
      .map(menu => {
        if (menu.subMenus) {
          return {
            ...menu,
            subMenus: menu.subMenus.filter(subMenu => hasFeatureAccess(subMenu.feature)),
          };
        }
        return menu;
      });
  };

  useEffect(() => {
    const filteredMenus = getFilteredMenus();
    handleSetActiveMenu(location.pathname, filteredMenus);
  }, [location, dataLogin]);

  const handleSetActiveMenu = (currentPath, menusToUse = menus) => {
    const updatedMenus = menusToUse.map((menu) => {
      if (menu.subMenus) {
        const isSubMenuActive = menu.subMenus.some((subMenu) =>
          currentPath.includes(subMenu.path)
        );

        return {
          ...menu,
          isOpen: isSubMenuActive,
          isActive: false,
          subMenus: menu.subMenus.map((subMenu) => ({
            ...subMenu,
            isActive: currentPath.includes(subMenu.path),
          })),
        };
      } else {
        return {
          ...menu,
          isActive: menu.path === currentPath,
          isOpen: menu.path === currentPath,
        };
      }
    });

    setMenus(updatedMenus);
  };

  const handleMenuClick = (menu, index) => {
    if (menu.subMenus) {
      const updatedMenus = menus.map((m, i) => {
        if (i === index) {
          return { ...m, isOpen: !m.isOpen };
        }
        return { ...m, isOpen: false }; // Close other menus
      });
      setMenus(updatedMenus);
    } else {
      const updatedMenus = menus.map((m) => ({
        ...m,
        isOpen: false, // Close all sub-menus
      }));
      setMenus(updatedMenus);
      navigate(menu.path);
    }
  };

  const handleSubMenuClick = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <div className="sidebar">
      <div>
        <div className="sidebar-logo">
          <Link to={FABRIC}>
            <img src={LaxmiLogo} height={100} width={100} alt="Logo" />
          </Link>
        </div>
        <hr style={{ color: "white", paddingBottom: "10px" }} />
        {menus.map((menu, index) => (
          <div key={index}>
            <div
              className={`sidebar-link ${
                menu.isActive ? "sidebar-active" : ""
              }`}
              onClick={() => handleMenuClick(menu, index)}
            >
              <div className="desc">
                <img src={menu.icon} alt="icon" />
                <p>{menu.title}</p>
                {menu.subMenus && (
                  <div
                    className={`dropdown-icon ${menu.isOpen ? "active" : ""}`}
                  ></div>
                )}
              </div>
            </div>
            {menu.subMenus && menu.isOpen && (
              <ul className="sub-menu">
                {menu.subMenus.map((subMenu, subIndex) => (
                  <li key={subIndex}>
                    <Link
                      to={subMenu.path}
                      onClick={() => handleSubMenuClick(subMenu.path)}
                      className={`submenu-link ${
                        subMenu.isActive ? "submenu-active" : ""
                      }`} // Apply active class here
                    >
                      <div className="sub-menu-link">
                        <p>{subMenu.title}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      <div className="d-flex flex-column">
        <div className="card-name">
          <div className="icon mr-2">
            <img src={UserAdmin} alt="User" />
          </div>
          <div className="d-flex flex-column">
            <div className="username">
              {CapitalizeWords(dataLogin.data.name)}
            </div>
            <div className="rolename">{dataLogin.data.role}</div>
          </div>
        </div>
        <div className="button-logout" onClick={handleLogout}>
          <div className="icon">
            <IconLogout className="icon-logout" />
          </div>
          <div className="text-logout">Logout</div>
        </div>
      </div>
    </div>
  );
};

export default SideBar;
